import React, {Fragment, useEffect, useState} from 'react';
import {Button, DialogContent, Typography} from '@material-ui/core';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {useTranslation} from 'react-i18next';
import {useApiNotification} from "../ui/ApiNotificationProvider";
import PermissionItem from "./permissions/PermissionItem";
import {getTopOrganizationUnits} from "../../api/organization-unit-functions";
import {PermissionTypes} from "../../auth/services/permission-types";
import {
    getAdminPermissions,
    getMultipleUnitsAdminPermissions,
    upsertAdmin
} from "../../api/organization-unit-admin-functions";
import * as _ from "lodash";
import PermissionHeaderItem from "./permissions/PermissionHeaderItem";
import AdminPermissionInfo from "./permissions/AdminPermissionInfo";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {emailRegex, run} from "../../utils/function-util";
import {useCapability} from "../../auth/CapabilityContext";
import {fetchUserCapabilities} from "../../api/user-capability-functions";
import {grey} from "@material-ui/core/colors";
import HelpOutline from "@material-ui/icons/HelpOutline";
import {KeyValue} from "../../models/shared";
import {OrgUnitPermission} from "../../models/admin-permission.models";
import {Roles} from "../../auth/roles";
import {OrgUnit} from "../../models/organization";
import Alert from "@material-ui/lab/Alert";
import {Domains} from "../../models/capabilities";
import {Navigate} from "react-router-dom";
import {TrimmedTextField} from "../_shared/TrimmedTextField";
import {LanguageSelector} from "../_shared/LanguageSelector";

const useStyles = makeStyles(theme => ({
    root: {
        '& .MuiTextField-root': {
            margin: theme.spacing(2),
            width: 300,
        },
        '& .MuiTextField-root input:read-only': {
            backgroundColor: grey[100],
            cursor: 'default'
        }
    },
    actions: {
        '& > *': {
            marginRight: theme.spacing(2),
        },
    }
}));


interface AdminForm {
    language: string,
    firstName: string,
    lastName: string,
    email: string,
    permissions: { [key: string]: OrgUnitPermission }
}

const AdminDetail = ({organizationId, adminId, onCancel}: any) => {
    const classes = useStyles();
    const {user, hasRole} = useCapability();
    const [viewOnly, setViewOnly] = useState(false);
    const {t, i18n} = useTranslation();
    const [admin, setAdmin] = useState<AdminForm>({language: i18n.language, firstName: '', lastName: '', email: '', permissions: {}});
    const [changeList, setChangeList] = useState<number[]>([]);
    const [userPermissions, setUserPermissions] = useState({});
    const [units, setUnits] = useState<OrgUnit[]>([]);
    const [errors, setErrors] = useState<KeyValue<string>>({});
    const [processing, setProcessing] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [finished, setFinished] = useState(false);
    const {handleError, showSuccess} = useApiNotification();
    const {capabilities, setCapabilities} = useCapability();
    const [AdminPermissionOpen, setAdminPermissionOpen] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));


    useEffect(() => user && hasRole ?
            setViewOnly(user.Id === adminId && !hasRole(Roles.ADMIN))
            : setViewOnly(true)
        , [user, hasRole]);

    useEffect(run(
            () => getTopOrganizationUnits(organizationId, PermissionTypes.ManagePermissions),
            setUnits,
            handleError),
        []);

    useEffect(run(async () => {
                return user?.Id && units && units.length ?
                    hasRole(Roles.ADMIN) ?
                        Promise.resolve({
                            permissions: units.map(u => ({
                                organizationUnitId: u.id,
                                explicitPermissions: PermissionTypes.All,
                                inheritedPermissions: PermissionTypes.All
                            }))
                        }) :
                        await getMultipleUnitsAdminPermissions(organizationId, user.Id, units)
                    : Promise.resolve({permissions: []})
            },
            (userPermission: { permissions: any[] }) => {
                const result = userPermission.permissions.reduce((acc, p) => ({...acc, [p.organizationUnitId]: p}), {});
                setUserPermissions(result);
            },
            handleError),
        [user, units]);

    useEffect(run(async () =>
                !!adminId && units && units.length ?
                    await getMultipleUnitsAdminPermissions(organizationId, adminId, units) :
                    Promise.resolve({permissions: []}),

            (adminPermission: any) => {
                const permissions = adminPermission.permissions.reduce((acc: any, p: any) => ({
                    ...acc,
                    [p.organizationUnitId]: p
                }), {});
                setAdmin({...adminPermission, permissions, language: admin.language});
            },
            handleError),
        [adminId, units]);


    const handleAdminPermissionClickOpen = () => {
        setAdminPermissionOpen(true);
    };

    const handleAdminPermissionClose = () => {
        setAdminPermissionOpen(false);
    };

    const handleInputChange = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        const {value, name} = e.target;
        handleFormChange(name, value);
    };

    const handleFormChange = (name: string, value: string) => {
        setAdmin(newAdmin => ({...newAdmin, ...{[name]: value}}));
        const validationErrors = validateField(name, value);
        setErrors((err) => ({...err, ...validationErrors}));
    };

    const validateField = (field: string, value: string): KeyValue<string> => {
        const validationErrors: KeyValue<string> = {};
        if (field === 'email') {
            validationErrors[field] = emailRegex.test(value) ? null : 'EmailIsInvalid';
        }
        else {
            validationErrors[field] = !value ? 'FieldIsRequired' : null;
        }
        return validationErrors;
    };

    const submit = async () => {
        setSubmitted(true);

        const validationErrors: KeyValue<string> = ['email', 'firstName', 'lastName', 'language']
            .reduce((acc, curr) => ({
                ...acc,
                ...validateField(curr, (admin as any)[curr])
            }), {});

        if (_.values(validationErrors).some((err: any) => !!err)) {
            setErrors(validationErrors);
            return;
        }

        const adminToUpsert = {
            ...admin,
            permissions: _.values(admin.permissions)
                .filter((p: any) => _.some(changeList, c => p.organizationUnitId === c))
                .filter((p: any) => p.explicitPermissions >= 0)
                .map((p: any) => ({
                    organizationUnitId: p.organizationUnitId,
                    permission: p.explicitPermissions
                }))
        };

        setProcessing(true);
        await upsertAdmin(organizationId, adminToUpsert)
            .then(r => {
                setFinished(true);
                showSuccess();
            })
            .catch(handleError);

        setCapabilities(await fetchUserCapabilities(organizationId));
        setProcessing(false);
        setChangeList([]);
    };

    const onPermissionExpanded = async (u: any, children: any[]) => {
        if (user) {
            let permissions = {}
            if (!hasRole(Roles.ADMIN)) {
                const resp = await getAdminPermissions(u.organizationId, user.Id, {organizationUnitParentId: u.id});
                permissions = resp.permissions.reduce((acc, p) => ({...acc, [p.organizationUnitId]: p}), {})
            } else {
                permissions = children.reduce((acc, c) => ({
                        ...acc,
                        [c.id]: {explicitPermissions: PermissionTypes.All, inheritedPermissions: PermissionTypes.All}
                    }),
                    {});
            }
            setUserPermissions(newUserPermissions => ({...newUserPermissions, ...permissions}))
        }

        if (adminId) {
            const adminPermission = await getAdminPermissions(u.organizationId, adminId, {organizationUnitParentId: u.id});
            const permissions = adminPermission.permissions.reduce((acc: any, p: any) => ({
                ...acc,
                [p.organizationUnitId]: p
            }), {});
            setAdmin(newAdmin => ({
                ...newAdmin,
                permissions: {
                    ...permissions,
                    ...newAdmin.permissions
                }
            }));
        }
    };

    const onChange = (u: any, p: any) => {
        setFinished(false);
        setAdmin(newAdmin => {
            const permissions = {
                ...newAdmin.permissions,
                [u.id]: {...newAdmin.permissions[u.id], organizationUnitId: u.id, explicitPermissions: p}
            };
            return ({
                ...newAdmin,
                permissions
            });
        });
        setChangeList(prev => Array.from(new Set<number>([...prev, u.id])));
    };

    if (!(hasRole(Roles.ADMIN) || _.size(capabilities[organizationId][Domains.Membership]) > 0)) {
        return <Navigate to={'/organizations'} replace={false}/>;
    }

    return (<Grid container spacing={3}>
        <Grid item xs={12} hidden={!viewOnly}>
            <Alert variant="filled" severity="warning">
                {t("Admin.DisallowForOneselfAlert")}
            </Alert>
        </Grid>
        <Grid item xs={12}>
            <Typography variant="h6">Admin Info</Typography>
            <form className={classes.root} noValidate autoComplete="off">
                <LanguageSelector name='language' error={submitted && !!(errors as any).language} value={admin.language}
                                  onFormChange={handleInputChange}
                                  label={t('_Common.Labels.Language')}
                                  helperText={submitted && (errors as any).language ? t(`_Common.Messages.${(errors as any).language}`) : null}
                                  inputProps={{readonly: !!adminId}}
                                  className='MuiTextField-root'  />
                <TrimmedTextField name='email' error={submitted && !!(errors as any).email} value={admin.email}
                                  onFormChange={(name: string, value: string) => handleFormChange(name, value)}
                                  label={t('_Common.Labels.Email')}
                                  helperText={submitted && (errors as any).email ? t(`_Common.Messages.${(errors as any).email}`) : null}
                                  inputProps={{readOnly: !!adminId}}/>
                <TrimmedTextField name='firstName' error={submitted && !!(errors as any).firstName}
                                  value={admin.firstName}
                                  onFormChange={(name: string, value: string) => handleFormChange(name, value)}
                                  label={t('_Common.Labels.FirstName')}
                                  helperText={submitted && (errors as any).firstName ? t(`_Common.Messages.${(errors as any).firstName}`) : null}
                                  inputProps={{readOnly: !!adminId}}/>
                <TrimmedTextField name='lastName' error={submitted && !!(errors as any).lastName} value={admin.lastName}
                                  onFormChange={(name: string, value: string) => handleFormChange(name, value)}
                                  label={t('_Common.Labels.LastName')}
                                  helperText={submitted && (errors as any).lastName ? t(`_Common.Messages.${(errors as any).lastName}`) : null}
                                  inputProps={{readOnly: !!adminId}}/>
            </form>
        </Grid>
        <Grid item xs={12}>
            <Typography variant="h6">
                {t('Admin.Description')}
                <IconButton color="primary" onClick={handleAdminPermissionClickOpen}>
                    <HelpOutline/>
                </IconButton>
            </Typography>
            <Dialog open={AdminPermissionOpen} onClose={handleAdminPermissionClose} fullScreen={fullScreen}>
                <DialogContent>
                    <AdminPermissionInfo onClose={handleAdminPermissionClose}/>
                </DialogContent>
            </Dialog>
        </Grid>
        <Grid xs={12} item={true} direction="column" justify="center" container>
            <PermissionHeaderItem availablePermissions={[PermissionTypes.PartialAdmin, PermissionTypes.Admin]}/>
            {units.map(u => {
                const inheritedPermissions = admin.permissions[(u as any).id] ? admin.permissions[(u as any).id].inheritedPermissions : 0;
                return <PermissionItem unit={u}
                                       viewOnly={viewOnly}
                                       key={(u as any).id.toString()}
                                       userPermissions={userPermissions}
                                       permissions={admin.permissions}
                                       inheritedPermissions={inheritedPermissions}
                                       onChange={onChange}
                                       onExpanded={onPermissionExpanded}
                                       availablePermissions={[PermissionTypes.PartialAdmin, PermissionTypes.Admin]}/>;
            })}
        </Grid>
        <Grid xs={12} item={true} container justify="flex-end" direction="row" className={classes.actions}>
            {finished ?
                <Button variant="outlined" color="primary" size="medium" onClick={onCancel}>
                    {t('_Common.Labels.Close')}
                </Button> :
                <Fragment>
                    <Button variant="outlined" color="primary" size="medium" onClick={onCancel} disabled={processing}>
                        {t('_Common.Labels.Cancel')}
                    </Button>
                    <Button variant="contained"
                            color="primary"
                            size="medium"
                            onClick={submit}
                            disabled={processing || viewOnly}>
                        {t('_Common.Labels.Save')}
                    </Button>
                </Fragment>}
        </Grid>
    </Grid>);
};
export default AdminDetail;
